import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Check, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAITransition } from "@/components/AITransition";

export default function ExperienceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playAI = useAITransition();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/experiences/${id}`)
      .then((r) => setExp(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="card-surface h-72 animate-pulse bg-linen-2" />
      </div>
    );
  }
  if (error || !exp) {
    return (
      <div className="max-w-4xl mx-auto py-24 px-4 text-center" data-testid="experience-not-found">
        <h1 className="font-display text-3xl text-charcoal mb-3">Experience not found</h1>
        <p className="text-charcoal/60 mb-6">This experience may have been removed or disabled.</p>
        <button onClick={() => navigate("/dashboard")} className="pill-btn pill-btn-primary">Back to home</button>
      </div>
    );
  }

  const inclusions = (exp.inclusions || []).filter((s) => String(s).trim());
  const priceBlock = exp.price_type === "on_request"
    ? { main: "Price to be confirmed at booking", sub: "Our concierge shares a quote once you book." }
    : exp.price_type === "approx"
      ? { main: `From ₹${Number(exp.price || 0).toLocaleString("en-IN")}`, sub: "Indicative price — final quote confirmed at booking." }
      : { main: `₹${Number(exp.price || 0).toLocaleString("en-IN")}`, sub: "Fixed price, confirmed at booking." };

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4 sm:px-6" data-testid="experience-detail">
      <button onClick={() => navigate("/dashboard")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal/60 hover:text-charcoal mt-6 transition-colors"
        data-testid="experience-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to home
      </button>

      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="mt-4 card-surface overflow-hidden">
        <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-linen-2 overflow-hidden" data-testid="experience-hero">
          {exp.image_url ? (
            <img src={exp.image_url} alt={exp.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-rose-300" strokeWidth={1.5} />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-charcoal/50 to-transparent" />
          <div className="absolute bottom-4 left-4 sm:left-6 right-4">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/80">Experience</p>
            <h1 className="font-display text-2xl sm:text-4xl text-white leading-tight" data-testid="experience-name">
              {exp.name}
            </h1>
          </div>
        </div>

        <div className="p-5 sm:p-8">
          {exp.tagline && (
            <p className="text-base sm:text-lg text-rose-600 font-semibold" data-testid="experience-tagline">
              {exp.tagline}
            </p>
          )}
          {exp.description && (
            <p className="mt-3 text-sm sm:text-base text-charcoal/75 leading-relaxed" data-testid="experience-description">
              {exp.description}
            </p>
          )}

          {inclusions.length > 0 && (
            <div className="mt-6" data-testid="experience-inclusions">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-3">
                What&apos;s included
              </p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {inclusions.map((inc, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="break-words min-w-0">{inc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Pricing */}
          <div className="mt-6 rounded-2xl border border-warm bg-linen-2/60 p-4 sm:p-5 flex items-center gap-4"
            data-testid="experience-pricing">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-charcoal/50">Pricing</p>
              <p className="font-display text-xl sm:text-2xl text-charcoal mt-0.5">{priceBlock.main}</p>
              <p className="text-xs text-charcoal/55 mt-1">{priceBlock.sub}</p>
            </div>
            {exp.price_type === "on_request" && (
              <Clock className="w-6 h-6 text-charcoal/30 flex-shrink-0" strokeWidth={1.75} />
            )}
          </div>

          {/* CTA — opens the EXISTING AI Planner with this experience as context */}
          <button
            onClick={(e) => playAI(() => navigate(`/planner?experience=${encodeURIComponent(exp.id)}`), e)}
            data-testid="plan-around-experience"
            className="mt-6 w-full rounded-full px-6 py-4 text-sm sm:text-base font-bold text-white shadow-lg shadow-rose-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}
          >
            <Sparkles className="w-5 h-5" strokeWidth={2.25} /> ✨ Plan a Celebration Around This
          </button>
          <p className="mt-3 text-center text-xs text-charcoal/50">
            CelebrAI will design the full plan and include this experience.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
